import React, { Suspense, lazy } from 'react';
import { MousePointerClick } from 'lucide-react';
import { EPCBadge } from './BuildingBadges';

const BuildingMapPanel = lazy(() => import('./BuildingMapPanel'));

/**
 * Map of the buildings shown, with a list beside it. A pin or a list item
 * opens the building's side panel.
 */
export const BuildingMapView = ({ buildings, openId, onOpen }) => {
  const open = (id) => {
    const building = buildings.find((b) => b.id === id);
    if (building) onOpen(building);
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[3fr_2fr] gap-4 items-start">
      <div className="card-base overflow-hidden">
        <Suspense fallback={
          <div className="h-[460px] flex items-center justify-center text-[12px] text-lb-muted">
            Loading map…
          </div>
        }>
          <BuildingMapPanel buildings={buildings} selectedBuildingId={openId} onSelectBuilding={open} />
        </Suspense>
      </div>
      <aside className="card-base max-h-[460px] overflow-y-auto scrollbar-thin" aria-label="Buildings on the map">
        {buildings.length === 0 ? (
          <div className="p-6 text-center">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-2"
              style={{ background: 'rgba(0,0,0,0.04)' }}
              aria-hidden="true"
            >
              <MousePointerClick size={18} strokeWidth={1.6} className="text-lb-ghost" />
            </div>
            <p className="text-[12px] text-lb-muted">No buildings to plot.</p>
          </div>
        ) : (
          <ul className="list-none m-0 p-0 divide-y divide-black/[0.06]">
            {buildings.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => onOpen(b)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left bg-transparent border-0 hover:bg-black/[0.02] focus-ring"
                  style={{ background: openId === b.id ? 'rgba(61,107,56,0.06)' : undefined }}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] font-semibold text-lb-ink-strong truncate">{b.name}</span>
                    <span className="block text-[12px] text-lb-muted mt-0.5 truncate">
                      {b.city || '—'}
                      {b.performance != null ? ` · ${b.performanceScore}% performance` : ''}
                      {!b.hasCoords ? ' · no location' : ''}
                    </span>
                  </span>
                  <EPCBadge rating={b.epc} compact />
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
};
